import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { useGameLogic } from '../hooks/useGameLogic';
import { signOut } from 'firebase/auth';
import { SoundControl } from '../components/SoundControl';
import { Link } from 'react-router-dom';

const LoginScreen = ({ currentUser, onJoinLobby, isMuted, toggleMute, playSound }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [roomCode, setRoomCode] = useState('');
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { createRoom, joinRoom } = useGameLogic(null, currentUser);

  useEffect(() => {
    if (email && !playerName) {
      setPlayerName(email.split('@')[0]);
    }
  }, [email, playerName]);

  // Google ile giriş fonksiyonu
  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      
      // Kullanıcı adını Google hesabından al
      if (result.user.displayName && !playerName) {
        setPlayerName(result.user.displayName);
      }
      
      playSound('success', 0.5);
    } catch (error) {
      console.error('Google giriş hatası:', error);
      
      if (error.code === 'auth/popup-closed-by-user') {
        setError('Giriş penceresi kapatıldı');
      } else if (error.code === 'auth/cancelled-popup-request') {
        setError('Giriş işlemi iptal edildi');
      } else {
        setError('Google ile giriş başarısız: ' + error.message);
      }
    }
    
    setLoading(false);
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Lütfen email ve şifre girin');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email, password);
      playSound('success', 0.5);
    } catch (error) {
      setError('Giriş başarısız: ' + error.message);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    if (!window.confirm('Çıkış yapmak istediğinize emin misiniz?')) {
      return;
    }

    try {
      localStorage.removeItem('axiati_room_code');
      localStorage.removeItem('axiati_screen');
      await signOut(auth);
    } catch (error) {
      alert('Çıkış hatası: ' + error.message);
    }
  };

  const handleRegister = async () => {
    if (!email || !password || !playerName) {
      setError('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(result.user, {
        displayName: playerName
      });
      playSound('success', 0.5);
    } catch (error) {
      setError('Kayıt başarısız: ' + error.message);
    }
    setLoading(false);
  };

  const handleCreateRoom = async () => {
    if (!playerName.trim()) {
      setError('Lütfen oyuncu adınızı girin');
      return;
    }

    if (playerName.trim().length < 2) {
      setError('Oyuncu adı en az 2 karakter olmalıdır');
      return;
    }

    if (playerName.trim().length > 15) {
      setError('Oyuncu adı en fazla 15 karakter olabilir');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const newRoomCode = await createRoom(playerName.trim());
      onJoinLobby(newRoomCode);
    } catch (error) {
      setError('Oda oluşturulamadı: ' + error.message);
    }
    setLoading(false);
  };

  const handleJoinRoom = async () => {
    if (!playerName.trim() || !roomCode.trim()) {
      setError('Lütfen oyuncu adı ve oda kodunu girin');
      return;
    }

    if (playerName.trim().length < 2) {
      setError('Oyuncu adı en az 2 karakter olmalıdır');
      return;
    }

    if (playerName.trim().length > 15) {
      setError('Oyuncu adı en fazla 15 karakter olabilir');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await joinRoom(roomCode.toUpperCase(), playerName.trim());
      onJoinLobby(roomCode.toUpperCase());
    } catch (error) {
      setError('Odaya katılamadı: ' + error.message);
    }
    setLoading(false);
  };

  // ===== LOGGED IN VIEW =====
  if (currentUser) {
    return (
      <div className="min-h-screen w-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <style>{`
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.95);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          .fade-in-scale {
            animation: fadeInScale 0.5s ease-out;
          }

          .glass-panel {
            background: rgba(15, 23, 42, 0.4);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(148, 163, 184, 0.1);
          }

          .input-field {
            background: rgba(30, 41, 59, 0.6);
            border: 1px solid rgba(148, 163, 184, 0.2);
            transition: all 0.3s ease;
          }

          .input-field:focus {
            border-color: rgba(99, 102, 241, 0.5);
            background: rgba(30, 41, 59, 0.8);
            box-shadow: 0 0 20px rgba(99, 102, 241, 0.1);
          }

          .btn-primary {
            background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
            transition: all 0.3s ease;
          }

          .btn-primary:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.3);
          }

          .btn-secondary {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            transition: all 0.3s ease;
          }

          .btn-secondary:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 20px 25px -5px rgba(16, 185, 129, 0.3);
          }

          .tab-button {
            position: relative;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .tab-button.active {
            color: #3b82f6;
          }

          .tab-button.active::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, #3b82f6 0%, #1e40af 100%);
            border-radius: 2px 2px 0 0;
          }

          .error-banner {
            background: rgba(239, 68, 68, 0.1);
            border-left: 3px solid #ef4444;
            animation: slideDown 0.3s ease;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .game-title {
            font-size: 2.5rem;
            font-weight: 800;
            background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin-bottom: 0.5rem;
            letter-spacing: 2px;
          }
        `}</style>

        <div className="w-full max-w-2xl fade-in-scale">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="logo-container">
              <img
                src="/logo-transparent.png"
                alt="Axio War Logo"
                className="logo-image"
              />
            </div>
            <h1 className="game-title">AXIO WAR</h1>
            <p className="text-slate-400 text-xs">Hoş geldiniz, {currentUser.displayName || 'Oyuncu'}</p>
          </div>

          {/* Main Card */}
          <div className="rounded-2xl p-6 md:p-8 mb-6">
            {/* Tab Navigation */}
            <div className="flex border-b border-slate-700 mb-6">
              <button
                onClick={() => {
                  playSound('click', 0.5);
                  setActiveTab('create');
                  setError('');
                }}
                className={`tab-button text-slate-400 ${activeTab === 'create' ? 'active' : ''}`}
              >
                🎮 Yeni Oda
              </button>
              <button
                onClick={() => {
                  playSound('click', 0.5);
                  setActiveTab('join');
                  setError('');
                }}
                className={`tab-button text-slate-400 ${activeTab === 'join' ? 'active' : ''}`}
              >
                🚪 Odaya Katıl
              </button>
            </div>

            {/* Create Room Tab */}
            {activeTab === 'create' && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <label className="block text-slate-300 text-sm font-semibold mb-2">
                    Oyuncu Adı
                  </label>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Oyuncu adınızı girin"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 input-field focus:outline-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">2-15 karakter arası</p>
                </div>

                <button
                  onClick={() => {
                    playSound('click', 0.5);
                    handleCreateRoom();
                  }}
                  disabled={loading}
                  className="w-full py-3 px-4 text-white font-bold rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Oda oluşturuluyor...
                    </>
                  ) : (
                    <>
                      🚀 Yeni Oda Oluştur
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Join Room Tab */}
            {activeTab === 'join' && (
              <div className="space-y-5 animate-fadeIn">
                <div>
                  <label className="block text-slate-300 text-sm font-semibold mb-2">
                    Oyuncu Adı
                  </label>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    placeholder="Oyuncu adınızı girin"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 input-field focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-semibold mb-2">
                    Oda Kodu
                  </label>
                  <input
                    type="text"
                    value={roomCode}
                    onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
                    placeholder="Oda kodunu girin (örn: ABC12345)"
                    className="w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 input-field focus:outline-none uppercase font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">Odanın sahibinden oda kodunu isteyin</p>
                </div>

                <button
                  onClick={() => {
                    playSound('click', 0.5);
                    handleJoinRoom();
                  }}
                  disabled={loading}
                  className="w-full py-3 px-4 text-white font-bold rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      Katılıyor...
                    </>
                  ) : (
                    <>
                      ✅ Odaya Katıl
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-banner rounded-lg p-4 mt-6">
                <div className="flex items-start gap-3">
                  <span className="text-xl">⚠️</span>
                  <div>
                    <p className="text-red-400 font-semibold text-sm">Hata</p>
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => {
                playSound('click', 0.5);
                handleLogout();
              }}
              className="flex-1 px-4 py-3 text-slate-300 font-semibold rounded-lg border border-slate-700 hover:border-slate-600 hover:text-slate-200 transition-all"
            >
              Çıkış Yap
            </button>

            <div className="flex items-center gap-2 px-4 py-3 glass-panel rounded-lg">
              <SoundControl isMuted={isMuted} onToggleMute={toggleMute} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== LOGIN/REGISTER VIEW =====
  return (
    <div className="min-h-screen w-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <style>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .fade-in-scale {
          animation: fadeInScale 0.5s ease-out;
        }

        .glass-panel {
          background: rgba(15, 23, 42, 0.4);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(148, 163, 184, 0.1);
        }

        .input-field {
          background: rgba(30, 41, 59, 0.6);
          border: 1px solid rgba(148, 163, 184, 0.2);
          transition: all 0.3s ease;
        }

        .input-field:focus {
          border-color: rgba(99, 102, 241, 0.5);
          background: rgba(30, 41, 59, 0.8);
          box-shadow: 0 0 20px rgba(99, 102, 241, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
          transition: all 0.3s ease;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(59, 130, 246, 0.3);
        }

        .btn-secondary {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          transition: all 0.3s ease;
        }

        .btn-secondary:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(16, 185, 129, 0.3);
        }

        .btn-google {
          background: white;
          color: #1f2937;
          border: 1px solid rgba(148, 163, 184, 0.2);
          transition: all 0.3s ease;
        }

        .btn-google:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 20px 25px -5px rgba(255, 255, 255, 0.2);
          background: #f8fafc;
        }

        .error-banner {
          background: rgba(239, 68, 68, 0.1);
          border-left: 3px solid #ef4444;
          animation: slideDown 0.3s ease;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .logo-container {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .logo-image {
          max-width: 480px;
          width: 100%;
          height: auto;
          filter: drop-shadow(0 0 20px rgba(59, 130, 246, 0.3));
        }

        .divider {
          display: flex;
          align-items: center;
          text-align: center;
          color: #64748b;
          font-size: 0.875rem;
          margin: 1.5rem 0;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid rgba(148, 163, 184, 0.2);
        }

        .divider span {
          padding: 0 1rem;
        }
      `}</style>

      <div className="w-full max-w-md fade-in-scale">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="logo-container">
            <img
              src="/logo-transparent.png"
              alt="Axio War Logo"
              className="logo-image"
            />
          </div>
          <h1 className="game-title">AXIO WAR</h1>
          <p className="text-slate-400 text-sm">Stratejik Savaş Oyunu</p>
        </div>

        {/* Main Card */}
        <div className="glass-panel rounded-2xl p-6 md:p-8 mb-6">
          <h2 className="text-2xl font-bold text-white mb-6">Hesabınıza Giriş Yapın</h2>

          {/* Google Login Button */}
          <button
            onClick={() => {
              playSound('click', 0.5);
              handleGoogleLogin();
            }}
            disabled={loading}
            className="w-full py-3 px-4 font-bold rounded-lg btn-google disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 mb-6"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19.8 10.2273C19.8 9.51819 19.7364 8.83637 19.6182 8.18182H10V12.05H15.5818C15.3273 13.3 14.5727 14.3591 13.4636 15.0682V17.5773H16.7727C18.7091 15.8364 19.8 13.2727 19.8 10.2273Z" fill="#4285F4"/>
              <path d="M10 20C12.7 20 14.9636 19.1045 16.7727 17.5773L13.4636 15.0682C12.5182 15.6682 11.3273 16.0227 10 16.0227C7.39545 16.0227 5.19091 14.2636 4.36364 11.9H0.957275V14.4909C2.75727 18.0591 6.11364 20 10 20Z" fill="#34A853"/>
              <path d="M4.36364 11.9C4.14545 11.3 4.02273 10.6591 4.02273 10C4.02273 9.34091 4.14545 8.7 4.36364 8.1V5.50909H0.957275C0.282729 6.85909 -0.0909576 8.38636 -0.0909576 10C-0.0909576 11.6136 0.282729 13.1409 0.957275 14.4909L4.36364 11.9Z" fill="#FBBC05"/>
              <path d="M10 3.97727C11.4682 3.97727 12.7864 4.48182 13.8227 5.47273L16.7455 2.55C14.9591 0.886364 12.6955 0 10 0C6.11364 0 2.75727 1.94091 0.957275 5.50909L4.36364 8.1C5.19091 5.73636 7.39545 3.97727 10 3.97727Z" fill="#EA4335"/>
            </svg>
            {loading ? 'Giriş yapılıyor...' : 'Google ile Giriş Yap'}
          </button>

          <div className="divider">
            <span>veya</span>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-slate-300 text-sm font-semibold mb-2">
                E-posta
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                className="w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 input-field focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-slate-300 text-sm font-semibold mb-2">
                Şifre
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Şifrenizi girin"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 input-field focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                onClick={() => {
                  playSound('click', 0.5);
                  handleLogin();
                }}
                disabled={loading}
                className="py-3 px-4 text-white font-bold rounded-lg btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '⏳' : '🔐 Giriş Yap'}
              </button>
              <button
                onClick={() => {
                  playSound('click', 0.5);
                  handleRegister();
                }}
                disabled={loading}
                className="py-3 px-4 text-white font-bold rounded-lg btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '⏳' : '✨ Kayıt Ol'}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="error-banner rounded-lg p-4 mt-6">
              <div className="flex items-start gap-3">
                <span className="text-xl">⚠️</span>
                <div>
                  <p className="text-red-400 font-semibold text-sm">Hata</p>
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-slate-400 text-sm">
            Hesabınız yok mu?{' '}
            <span className="text-slate-500">Kayıt olmak için yukarıdaki formu kullanın</span>
          </p>
        </div>
        <div className="text-center mt-6 p-4 border-t border-slate-700/50">
          <p className="text-slate-500 text-xs">
            Hizmete devam ederek Axiowar'ın
            <Link
              to="/kullanim-sartlari"
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors mx-1"
            >
              Kullanım Şartları
            </Link>
            ve
            <Link
              to="/gizlilik-politikasi"
              className="text-blue-400 hover:text-blue-300 font-semibold transition-colors mx-1"
            >
              Gizlilik Politikası
            </Link>
            'nı kabul etmiş olursunuz.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;